import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Terminal, Trash2, Building2, Upload, Wand2, AlertTriangle, X, ArrowLeftRight, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useCurrency } from "@/lib/currency";

const PROVIDERS = ["eftpos", "square", "stripe", "tyro", "commbank", "westpac", "manual", "other"];

const fmt_static = (c) => `SBD ${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function MoneyReconciliation() {
  const [terminals, setTerminals] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tOpen, setTOpen] = useState(false);
  const [sOpen, setSOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [importing, setImporting] = useState(false);
  const [matching, setMatching] = useState(false);
  const fileRef = useRef(null);
  const pdfRef = useRef(null);
  const [importTerm, setImportTerm] = useState("");
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillRow, setDrillRow] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const { display, other, rate, toggle, fmt } = useCurrency({ base: "SBD", other: "AUD" });

  const load = useCallback(async () => {
    const [t, s] = await Promise.all([
      api.get("/money/terminals"),
      api.get("/money/settlements", { params: filter !== "all" ? { status: filter } : {} }),
    ]);
    setTerminals(t.data);
    setRows(s.data.settlements);
    setSummary(s.data.summary);
    setLoading(false);
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const tById = Object.fromEntries(terminals.map((t) => [t.id, t]));

  const removeTerminal = async (id, name) => {
    if (!confirm(`Delete terminal "${name}"?`)) return;
    await api.delete(`/money/terminals/${id}`);
    toast.success("Terminal deleted");
    load();
  };
  const removeSettlement = async (id) => {
    if (!confirm("Delete this settlement row?")) return;
    await api.delete(`/money/settlements/${id}`);
    toast.success("Settlement deleted");
    load();
  };

  const runMatch = async () => {
    setMatching(true);
    try {
      const r = await api.post("/money/reconciliation/match?window_days=3&amount_tolerance_cents=50");
      const { matched, fee_variance, missing_deposit, duplicate } = r.data;
      toast.success(`${matched} matched · ${fee_variance} fee variance · ${missing_deposit} missing · ${duplicate} duplicate`);
      load();
    } catch (e) { toast.error("Match failed"); }
    finally { setMatching(false); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !importTerm) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("upload", file);
      const r = await api.post(`/money/settlements/import?terminal_id=${importTerm}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${r.data.imported} rows · Auto-matched ${r.data.auto_matched} settlements`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  const [pdfPreview, setPdfPreview] = useState(null);   // { rows, filename, accountId, selected: Set<idx> }

  const handlePdf = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const accs = await api.get("/money/accounts");
    const bank = accs.data.find((a) => a.type === "bank") || accs.data[0];
    if (!bank) { toast.error("No bank account"); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("upload", file);
      const r = await api.post("/money/deposits/import-pdf/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // pre-select everything, user can uncheck
      setPdfPreview({
        rows: r.data.candidates, filename: file.name, accountId: bank.id,
        selected: new Set(r.data.candidates.map((_, i) => i)),
      });
    } catch (err) { toast.error(err?.response?.data?.detail || "PDF parse failed"); }
    finally { setImporting(false); }
  };

  const commitPdf = async () => {
    const chosen = pdfPreview.rows.filter((_, i) => pdfPreview.selected.has(i));
    setImporting(true);
    try {
      const r = await api.post(`/money/deposits/import-pdf/commit?account_id=${pdfPreview.accountId}`, { rows: chosen });
      toast.success(`Imported ${r.data.inserted} · Auto-matched ${r.data.auto_matched} settlements`);
      setPdfPreview(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  const openDrill = (row) => { setDrillRow(row); setDrillOpen(true); };
  const toggleSel = (id) => {
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const bulkResolve = async () => {
    const ids = [...selected];
    const r = await api.post("/money/settlements/bulk-resolve", { ids });
    toast.success(`Resolved ${r.data.resolved} exceptions`);
    setSelected(new Set()); load();
  };
  const bulkWriteOff = async () => {
    const ids = [...selected];
    const r = await api.post("/money/settlements/bulk-write-off", { ids });
    toast.success(`Wrote off ${r.data.written_off} · Total ${fmt(r.data.total_amount_cents)}`);
    setSelected(new Set()); load();
  };
  const [bulkPickerOpen, setBulkPickerOpen] = useState(false);
  const [bulkCandidates, setBulkCandidates] = useState([]);
  const openBulkReassign = async () => {
    const r = await api.get("/money/transactions?direction=income&limit=200");
    setBulkCandidates(r.data); setBulkPickerOpen(true);
  };
  const doBulkReassign = async (txId) => {
    const ids = [...selected];
    const r = await api.post("/money/settlements/bulk-reassign", { ids, transaction_id: txId });
    toast.success(`Re-paired ${r.data.reassigned} settlements · Δ ${fmt(r.data.total_variance_cents)}`);
    setBulkPickerOpen(false); setSelected(new Set()); load();
  };
  const drillResolve = async () => {
    await api.post(`/money/settlements/${drillRow.id}/resolve`);
    toast.success("Exception resolved");
    setDrillOpen(false); load();
  };
  const drillWriteOff = async () => {
    const r = await api.post(`/money/settlements/${drillRow.id}/write-off`);
    toast.success(`Write-off posted · ${fmt(r.data.amount_cents)}`);
    setDrillOpen(false); load();
  };

  if (loading) return <div className="text-sm text-surface-onVariant">Loading reconciliation…</div>;

  return (
    <div className="animate-fade-in-up space-y-8">
      {/* Terminals */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-lg font-medium text-surface-on tracking-tight">Terminals</h3>
            <p className="text-xs text-surface-onVariant">Every POS, EFTPOS or online payment terminal you settle from</p>
          </div>
          <button
            data-testid="add-terminal"
            onClick={() => setTOpen(true)}
            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground text-sm font-medium px-4 py-2 hover:brightness-110 active:scale-[0.98] transition-[transform,filter] duration-200 ease-md"
          >
            <Plus className="w-4 h-4" /> Add terminal
          </button>
        </div>
        {terminals.length === 0 ? (
          <div className="rounded-hero bg-surface-container p-10 text-center">
            <Terminal className="w-8 h-8 mx-auto text-surface-onVariant" />
            <p className="text-sm text-surface-onVariant mt-3">No terminals yet. Add one to start recording settlements.</p>
          </div>
        ) : (
          <ul className="grid md:grid-cols-2 gap-3" data-testid="terminals-list">
            {terminals.map((t) => (
              <li key={t.id} className="bg-surface-container rounded-hero p-5 flex items-start gap-4">
                <div className="w-10 h-10 rounded-2xl bg-primary-container text-primary-onContainer flex items-center justify-center shrink-0">
                  <Building2 className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-surface-on truncate">{t.name}</div>
                  <div className="text-[11px] uppercase tracking-widest text-surface-onVariant mt-0.5">{t.provider}{t.merchant_ref ? ` · ${t.merchant_ref}` : ""}</div>
                  {t.notes && <div className="text-xs text-surface-onVariant mt-2">{t.notes}</div>}
                </div>
                <button onClick={() => removeTerminal(t.id, t.name)} className="p-1.5 rounded-full text-surface-onVariant hover:bg-danger-container hover:text-danger transition-colors" aria-label="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Settlements */}
      <section>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="text-lg font-medium text-surface-on tracking-tight">Settlements</h3>
            <p className="text-xs text-surface-onVariant">Canonical daily payout rows — the source of truth for reconciliation</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid="currency-toggle-recon" onClick={toggle}
              className="inline-flex items-center gap-1 rounded-full bg-surface-container hover:bg-primary/8 text-surface-on text-xs font-medium px-3 py-1.5 transition-colors border border-outline-variant"
              title={rate ? `1 SBD = ${rate.toFixed(4)} AUD` : ""}>
              <ArrowLeftRight className="w-3.5 h-3.5" />
              View in {other}
            </button>
            <div className="hidden md:flex items-center gap-1 bg-surface-container rounded-full pl-3 pr-1 py-1 border border-outline-variant">
              <Select value={importTerm} onValueChange={setImportTerm}>
                <SelectTrigger className="w-40 border-none bg-transparent h-7 px-1 text-xs" data-testid="import-terminal-select"><SelectValue placeholder="For terminal…" /></SelectTrigger>
                <SelectContent className="rounded-2xl bg-surface">
                  {terminals.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <input ref={fileRef} type="file" hidden accept=".csv,.xlsx" onChange={handleImport} data-testid="settlements-import-file" />
              <button data-testid="import-btn" onClick={() => fileRef.current?.click()} disabled={!importTerm || importing}
                className="inline-flex items-center gap-1 rounded-full bg-surface-containerHigh text-surface-on text-xs font-medium px-3 py-1 hover:bg-primary/8 disabled:opacity-40 transition-colors">
                <Upload className="w-3.5 h-3.5" /> {importing ? "Importing…" : "CSV/XLSX"}
              </button>
            </div>
            <input ref={pdfRef} type="file" hidden accept=".pdf" onChange={handlePdf} data-testid="pdf-import-file" />
            <button data-testid="pdf-import-btn" onClick={() => pdfRef.current?.click()} disabled={importing}
              className="inline-flex items-center gap-2 rounded-full bg-surface-container hover:bg-primary/8 text-surface-on text-sm font-medium px-3 py-2 disabled:opacity-40 transition-colors border border-outline-variant">
              <FileText className="w-4 h-4" /> Import PDF statement
            </button>
            <button data-testid="run-match-btn" onClick={runMatch} disabled={matching}
              className="inline-flex items-center gap-2 rounded-full bg-primary-container text-primary-onContainer text-sm font-medium px-4 py-2 hover:brightness-110 active:scale-[0.98] disabled:opacity-40 transition-[transform,filter] duration-200 ease-md">
              <Wand2 className="w-4 h-4" /> {matching ? "Matching…" : "Run match"}
            </button>
            <button
              data-testid="add-settlement"
              onClick={() => setSOpen(true)}
              disabled={terminals.length === 0}
              className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground text-sm font-medium px-4 py-2 hover:brightness-110 active:scale-[0.98] disabled:opacity-40 transition-[transform,filter] duration-200 ease-md"
            >
              <Plus className="w-4 h-4" /> Add settlement
            </button>
          </div>
        </div>

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatCard label="Rows" value={summary.count} />
            <StatCard label="Gross" value={fmt(summary.gross_cents)} />
            <StatCard label="Fees" value={fmt(summary.fees_cents)} tone="danger" />
            <StatCard label="Net" value={fmt(summary.net_cents)} tone="primary" />
          </div>
        )}

        <div className="inline-flex items-center gap-1 rounded-full bg-surface-container p-1 mb-3">
          {[["all", "All"], ["unmatched", "Unmatched"], ["matched", "Matched"], ["exception", "Exceptions"]].map(([v, l]) => (
            <button key={v} data-testid={`filter-${v}`} onClick={() => setFilter(v)}
              className={cn("px-3 py-1.5 rounded-full text-xs font-medium transition-colors",
                filter === v ? "bg-primary text-primary-foreground" : "text-surface-on hover:bg-primary/8")}>
              {l}
            </button>
          ))}
        </div>

        {selected.size > 0 && (
          <div className="mb-3 rounded-2xl bg-primary-container/50 border border-primary/30 px-4 py-2 flex items-center gap-3" data-testid="bulk-actions-bar">
            <span className="text-sm font-medium text-primary-onContainer">{selected.size} selected</span>
            <button data-testid="bulk-resolve" onClick={bulkResolve} className="ml-auto inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground text-sm font-medium px-4 py-1.5 hover:brightness-110 active:scale-[.98] transition-[transform,filter] duration-200">
              Mark resolved
            </button>
            <button data-testid="bulk-writeoff" onClick={bulkWriteOff} className="inline-flex items-center gap-2 rounded-full bg-surface text-surface-on hover:bg-surface-container text-sm font-medium px-4 py-1.5 transition-colors border border-outline-variant">
              Post write-off
            </button>
            <button data-testid="bulk-reassign" onClick={openBulkReassign} className="inline-flex items-center gap-2 rounded-full bg-surface text-surface-on hover:bg-surface-container text-sm font-medium px-4 py-1.5 transition-colors border border-outline-variant">
              Reassign to one deposit…
            </button>
            <button onClick={() => setSelected(new Set())} className="text-xs text-surface-onVariant hover:text-surface-on transition-colors">Clear</button>
          </div>
        )}

        {rows.length === 0 ? (
          <div className="rounded-hero bg-surface-container p-10 text-center">
            <p className="text-sm text-surface-onVariant">No settlements in this view.</p>
          </div>
        ) : (
          <div className="bg-surface-container rounded-hero overflow-hidden" data-testid="settlements-table">
            <table className="w-full text-sm">
              <thead className="bg-surface-containerHigh text-[10px] uppercase tracking-widest text-surface-onVariant">
                <tr>
                  <th className="pl-4 py-2 w-8"></th>
                  <th className="text-left px-4 py-2">Date</th>
                  <th className="text-left px-4 py-2">Terminal</th>
                  <th className="text-right px-4 py-2">Gross</th>
                  <th className="text-right px-4 py-2">Fees</th>
                  <th className="text-right px-4 py-2">Net</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => {
                  const status = s.exception_code ? "exception" : (s.matched_transaction_id ? "matched" : "unmatched");
                  const tone = status === "matched" ? "bg-primary-container text-primary-onContainer" :
                               status === "exception" ? "bg-danger-container text-danger" :
                               "bg-surface-containerHigh text-surface-onVariant";
                  const excLabel = s.exception_code ? s.exception_code.replace("_", " ") : status;
                  const clickable = s.exception_code || !s.matched_transaction_id;
                  return (
                    <tr key={s.id} data-testid={`settlement-row-${s.id}`}
                      className={cn("border-t border-outline-variant/40 transition-colors", clickable && "cursor-pointer hover:bg-primary/5", selected.has(s.id) && "bg-primary/8")}
                      onClick={clickable ? () => openDrill(s) : undefined}>
                      <td className="pl-4 py-3 w-8" onClick={(e) => e.stopPropagation()}>
                        {s.exception_code && (
                          <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggleSel(s.id)} className="accent-primary" data-testid={`sel-${s.id}`} />
                        )}
                      </td>
                      <td className="px-4 py-3 text-surface-on">{s.settlement_date}</td>
                      <td className="px-4 py-3 text-surface-on truncate max-w-[180px]">{tById[s.terminal_id]?.name || "—"}</td>
                      <td className="px-4 py-3 text-right text-surface-on tabular-nums">{fmt(s.gross_cents)}</td>
                      <td className="px-4 py-3 text-right text-danger tabular-nums">{fmt(s.fees_cents)}</td>
                      <td className="px-4 py-3 text-right text-surface-on tabular-nums font-medium">{fmt(s.net_cents)}</td>
                      <td className="px-4 py-3">
                        <span className={cn("text-[10px] font-medium tracking-wider uppercase px-2 py-0.5 rounded-full inline-flex items-center gap-1", tone)}>
                          {s.exception_code && <AlertTriangle className="w-3 h-3" />}
                          {excLabel}
                        </span>
                        {s.variance_cents !== undefined && s.variance_cents !== null && (
                          <div className="text-[10px] text-surface-onVariant mt-1 tabular-nums">
                            Δ {s.variance_cents > 0 ? "+" : ""}{fmt(s.variance_cents)}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-3 text-right">
                        <button onClick={(e) => { e.stopPropagation(); removeSettlement(s.id); }} className="p-1.5 rounded-full text-surface-onVariant hover:bg-danger-container hover:text-danger transition-colors" aria-label="Delete">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <TerminalDialog open={tOpen} onOpenChange={setTOpen} onSaved={load} />
      <SettlementDialog open={sOpen} onOpenChange={setSOpen} terminals={terminals} onSaved={load} />
      <ExceptionDialog open={drillOpen} onOpenChange={setDrillOpen} row={drillRow} terminals={tById} fmt={fmt} onResolve={drillResolve} onWriteOff={drillWriteOff} onReload={load} />
      <PdfPreviewDialog preview={pdfPreview} setPreview={setPdfPreview} fmt={fmt} onCommit={commitPdf} importing={importing} />
      <Dialog open={bulkPickerOpen} onOpenChange={setBulkPickerOpen}>
        <DialogContent className="max-w-lg max-h-[70vh] bg-surface rounded-hero border-none p-6 flex flex-col" data-testid="bulk-reassign-dialog">
          <DialogHeader><DialogTitle className="text-surface-on text-lg font-medium">Reassign {selected.size} settlements</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-surface-onVariant">Pick the single bank deposit these settlements collapse onto.</DialogDescription>
          <div className="flex-1 overflow-y-auto mt-4 space-y-1">
            {bulkCandidates.map((t) => (
              <button key={t.id} data-testid={`bulk-reassign-${t.id}`} onClick={() => doBulkReassign(t.id)}
                className="w-full text-left flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-primary/8 transition-colors">
                <span className="text-xs text-surface-onVariant tabular-nums w-24">{t.date}</span>
                <span className="flex-1 text-sm text-surface-on truncate">{t.payee}</span>
                <span className="text-sm font-medium tabular-nums text-surface-on">{fmt(t.amount_cents)}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PdfPreviewDialog({ preview, setPreview, fmt, onCommit, importing }) {
  if (!preview) return null;
  const toggle = (i) => {
    const s = new Set(preview.selected);
    if (s.has(i)) s.delete(i); else s.add(i);
    setPreview({ ...preview, selected: s });
  };
  const toggleAll = () => {
    if (preview.selected.size === preview.rows.length) setPreview({ ...preview, selected: new Set() });
    else setPreview({ ...preview, selected: new Set(preview.rows.map((_, i) => i)) });
  };
  const totIn  = preview.rows.filter((r, i) => preview.selected.has(i) && r.direction === "income").reduce((s, r) => s + r.amount_cents, 0);
  const totOut = preview.rows.filter((r, i) => preview.selected.has(i) && r.direction === "expense").reduce((s, r) => s + r.amount_cents, 0);
  return (
    <Dialog open={true} onOpenChange={(v) => !v && setPreview(null)}>
      <DialogContent className="max-w-3xl max-h-[85vh] bg-surface rounded-hero border-none p-6 flex flex-col" data-testid="pdf-preview-dialog">
        <DialogHeader><DialogTitle className="text-surface-on text-lg font-medium">Confirm PDF import</DialogTitle></DialogHeader>
        <DialogDescription className="text-sm text-surface-onVariant">
          {preview.rows.length} candidate rows parsed from <span className="font-medium">{preview.filename}</span>. Uncheck any noise before saving.
        </DialogDescription>
        <div className="mt-3 flex items-center gap-3">
          <button onClick={toggleAll} data-testid="pdf-select-all" className="text-xs text-primary hover:underline">
            {preview.selected.size === preview.rows.length ? "Deselect all" : "Select all"}
          </button>
          <span className="text-xs text-surface-onVariant">·</span>
          <span className="text-xs text-surface-onVariant">{preview.selected.size} selected · {fmt(totIn)} in · {fmt(totOut)} out</span>
        </div>
        <div className="flex-1 overflow-y-auto mt-4 border-t border-outline-variant/40" data-testid="pdf-preview-list">
          <table className="w-full text-sm">
            <tbody>
              {preview.rows.map((row, i) => (
                <tr key={i} className={cn("border-b border-outline-variant/40 transition-colors", preview.selected.has(i) && "bg-primary/5")}>
                  <td className="pl-4 py-2 w-8">
                    <input type="checkbox" checked={preview.selected.has(i)} onChange={() => toggle(i)} className="accent-primary" data-testid={`pdf-row-${i}`} />
                  </td>
                  <td className="px-3 py-2 text-xs text-surface-onVariant tabular-nums">{row.date}</td>
                  <td className="px-3 py-2 text-sm text-surface-on truncate max-w-[300px]">{row.payee}</td>
                  <td className={cn("px-3 py-2 text-xs uppercase tracking-widest", row.direction === "income" ? "text-[#0F5027]" : "text-danger")}>{row.direction}</td>
                  <td className="px-3 py-2 text-right text-sm font-medium tabular-nums text-surface-on">{fmt(row.amount_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex items-center gap-2 pt-3 border-t border-outline-variant">
          <button onClick={onCommit} disabled={importing || preview.selected.size === 0} data-testid="pdf-commit-btn"
            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground text-sm font-medium px-4 py-2 hover:brightness-110 active:scale-[0.98] disabled:opacity-40 transition-[transform,filter] duration-200 ease-md">
            {importing ? "Importing…" : `Import ${preview.selected.size} rows & auto-match`}
          </button>
          <button onClick={() => setPreview(null)} className="text-sm text-surface-onVariant hover:text-surface-on transition-colors ml-auto">Cancel</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ExceptionDialog({ open, onOpenChange, row, terminals, fmt, onResolve, onWriteOff, onReload }) {
  const [journals, setJournals] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  useEffect(() => {
    if (!open || !row) return;
    setPickerOpen(false);
    api.get(`/money/journals?settlement_id=${row.id}`).then((r) => setJournals(r.data)).catch(() => setJournals([]));
  }, [open, row]);
  const loadCandidates = async () => {
    if (!row) return;
    // fetch income transactions within ±7 days of the settlement date
    const d = new Date(row.settlement_date);
    const from_ = new Date(d.getTime() - 7 * 86400000).toISOString().split("T")[0];
    const to = new Date(d.getTime() + 7 * 86400000).toISOString().split("T")[0];
    const r = await api.get(`/money/transactions?direction=income&limit=100`);
    // client-side date filter (backend accepts month-only, so we filter here)
    setCandidates(r.data.filter((t) => t.date >= from_ && t.date <= to));
    setPickerOpen(true);
  };
  const doReassign = async (txId) => {
    await api.post(`/money/settlements/${row.id}/reassign?transaction_id=${txId}`);
    toast.success("Re-paired");
    onOpenChange(false);
    onReload && onReload();
  };
  if (!row) return null;
  const term = terminals[row.terminal_id];
  const code = row.exception_code;
  const codeMeta = {
    fee_variance:    { label: "Fee variance",    tip: "Bank deposit differs from settlement net. Likely a fee schedule change or partial deposit." },
    missing_deposit: { label: "Missing deposit", tip: "No bank deposit lands within ±3 days of this settlement. Bank may still be processing." },
    duplicate:       { label: "Duplicate row",   tip: "A settlement with identical terminal, date and amount already exists. Investigate before writing off." },
  }[code] || { label: "Needs review", tip: "" };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-surface rounded-hero border-none p-6" data-testid="exception-dialog">
        <DialogHeader><DialogTitle className="text-surface-on text-xl font-medium flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-danger" /> {codeMeta.label}
        </DialogTitle></DialogHeader>
        <DialogDescription className="text-sm text-surface-onVariant mt-1">{codeMeta.tip}</DialogDescription>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <Cell label="Terminal" value={term?.name || "—"} />
          <Cell label="Settlement date" value={row.settlement_date} />
          <Cell label="Gross" value={fmt(row.gross_cents)} />
          <Cell label="Fees" value={fmt(row.fees_cents)} />
          <Cell label="Net expected" value={fmt(row.net_cents)} highlight />
          <Cell label="Variance" value={row.variance_cents ? `${row.variance_cents > 0 ? "+" : ""}${fmt(row.variance_cents)}` : "—"} tone={row.variance_cents ? "danger" : ""} />
        </div>

        {journals.length > 0 && (
          <div className="mt-4 bg-surface-container rounded-2xl p-4">
            <div className="text-[10px] uppercase tracking-widest text-surface-onVariant mb-2">Auto-posted journal</div>
            <div className="space-y-1 text-sm">
              {journals[0].lines.map((l, i) => (
                <div key={i} className="flex items-center justify-between font-mono text-xs">
                  <span className={cn("px-2 py-0.5 rounded", l.side === "debit" ? "bg-surface-containerHigh text-surface-on" : "bg-primary-container text-primary-onContainer")}>
                    {l.side.toUpperCase()}
                  </span>
                  <span className="flex-1 mx-3 truncate text-surface-onVariant">{l.memo}</span>
                  <span className="tabular-nums text-surface-on">{fmt(l.amount_cents)}</span>
                </div>
              ))}
              <div className="border-t border-outline-variant/40 mt-2 pt-2 flex items-center justify-between text-xs">
                <span className="text-surface-onVariant">Balance</span>
                <span className={cn("font-medium tabular-nums", journals[0].total_debit_cents === journals[0].total_credit_cents ? "text-[#0F5027]" : "text-danger")}>
                  Dr {fmt(journals[0].total_debit_cents)} = Cr {fmt(journals[0].total_credit_cents)}
                </span>
              </div>
            </div>
          </div>
        )}

        {pickerOpen && (
          <div className="mt-4 bg-surface-container rounded-2xl p-4 max-h-64 overflow-y-auto" data-testid="reassign-picker">
            <div className="text-[10px] uppercase tracking-widest text-surface-onVariant mb-2">Pick a different deposit</div>
            {candidates.length === 0 ? (
              <p className="text-xs text-surface-onVariant">No income transactions found in ±7 days.</p>
            ) : (
              <ul className="space-y-1">
                {candidates.map((t) => (
                  <li key={t.id}>
                    <button onClick={() => doReassign(t.id)} data-testid={`reassign-${t.id}`}
                      className="w-full text-left flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-primary/8 transition-colors">
                      <span className="text-xs text-surface-onVariant tabular-nums w-24">{t.date}</span>
                      <span className="flex-1 text-sm text-surface-on truncate">{t.payee}</span>
                      <span className="text-sm font-medium tabular-nums text-surface-on">{fmt(t.amount_cents)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="mt-5 flex flex-wrap items-center gap-2">
          <button data-testid="drill-resolve" onClick={onResolve} className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground text-sm font-medium px-4 py-2 hover:brightness-110 active:scale-[0.98] transition-[transform,filter] duration-200 ease-md">
            Mark resolved
          </button>
          <button data-testid="drill-writeoff" onClick={onWriteOff} className="inline-flex items-center gap-2 rounded-full bg-surface-container text-surface-on hover:bg-primary/8 text-sm font-medium px-4 py-2 transition-colors border border-outline-variant">
            Post write-off
          </button>
          <button data-testid="drill-reassign" onClick={loadCandidates} className="inline-flex items-center gap-2 rounded-full bg-surface-container text-surface-on hover:bg-primary/8 text-sm font-medium px-4 py-2 transition-colors border border-outline-variant">
            Reassign…
          </button>
          <button onClick={() => onOpenChange(false)} className="ml-auto text-sm text-surface-onVariant hover:text-surface-on transition-colors">Close</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Cell({ label, value, tone = "", highlight = false }) {
  const cls = tone === "danger" ? "text-danger" : tone === "primary" ? "text-primary" : "text-surface-on";
  return (
    <div className={cn("rounded-2xl px-4 py-3", highlight ? "bg-primary-container/50" : "bg-surface-container")}>
      <div className="text-[10px] uppercase tracking-widest text-surface-onVariant">{label}</div>
      <div className={cn("text-base font-medium tabular-nums mt-1", cls)}>{value}</div>
    </div>
  );
}

function StatCard({ label, value, tone = "" }) {
  return (
    <div className="bg-surface-container rounded-2xl px-4 py-3">
      <div className="text-[10px] uppercase tracking-widest text-surface-onVariant">{label}</div>
      <div className={cn("text-lg font-medium tabular-nums mt-1",
        tone === "primary" ? "text-primary" : tone === "danger" ? "text-danger" : "text-surface-on")}>
        {value}
      </div>
    </div>
  );
}

function TerminalDialog({ open, onOpenChange, onSaved }) {
  const [name, setName] = useState("");
  const [provider, setProvider] = useState("manual");
  const [merchantRef, setMerchantRef] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api.post("/money/terminals", { name: name.trim(), provider, merchant_ref: merchantRef.trim() || null, notes: notes.trim() || null });
      toast.success(`Terminal "${name.trim()}" added`);
      setName(""); setProvider("manual"); setMerchantRef(""); setNotes("");
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-surface rounded-hero border-none p-6" data-testid="terminal-dialog">
        <DialogHeader><DialogTitle className="text-surface-on text-lg font-medium">Add terminal</DialogTitle></DialogHeader>
        <DialogDescription className="text-sm text-surface-onVariant mt-1">Register a payment device or online payment source that produces daily settlements.</DialogDescription>
        <form onSubmit={submit} className="mt-5 space-y-4">
          <Field label="Name">
            <input data-testid="terminal-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="e.g. Bloom Roasters — Main POS" className="bg-transparent outline-none w-full text-surface-on py-2 border-b-2 border-outline focus:border-primary" />
          </Field>
          <Field label="Provider">
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger data-testid="terminal-provider" className="w-full bg-surface-containerHigh border-b-2 border-outline focus:border-primary rounded-t-lg h-11 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-2xl bg-surface">
                {PROVIDERS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Merchant reference (optional)">
            <input value={merchantRef} onChange={(e) => setMerchantRef(e.target.value)} placeholder="Serial, MID or nickname" className="bg-transparent outline-none w-full text-surface-on py-2 border-b-2 border-outline focus:border-primary" />
          </Field>
          <Field label="Notes (optional)">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="bg-transparent outline-none w-full text-surface-on py-2 border-b-2 border-outline focus:border-primary resize-none" />
          </Field>
          <button data-testid="terminal-save" type="submit" disabled={saving || !name.trim()} className="w-full rounded-full bg-primary text-primary-foreground font-medium py-3 active:scale-[0.98] hover:brightness-110 disabled:opacity-60 transition-[transform,filter] duration-200 ease-md">
            {saving ? "Saving…" : "Add terminal"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SettlementDialog({ open, onOpenChange, terminals, onSaved }) {
  const [terminalId, setTerminalId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [gross, setGross] = useState("");
  const [fees, setFees] = useState("");
  const [txCount, setTxCount] = useState("");
  const [ref, setRef] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!terminalId && terminals.length) setTerminalId(terminals[0].id); }, [terminals, terminalId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!terminalId || !gross) return;
    setSaving(true);
    try {
      await api.post("/money/settlements", {
        terminal_id: terminalId, settlement_date: date,
        gross_cents: Math.round(parseFloat(gross) * 100),
        fees_cents: Math.round(parseFloat(fees || 0) * 100),
        tx_count: parseInt(txCount || 0, 10),
        external_ref: ref.trim() || null,
      });
      toast.success("Settlement recorded");
      setGross(""); setFees(""); setTxCount(""); setRef("");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-surface rounded-hero border-none p-6" data-testid="settlement-dialog">
        <DialogHeader><DialogTitle className="text-surface-on text-lg font-medium">Record settlement</DialogTitle></DialogHeader>
        <DialogDescription className="text-sm text-surface-onVariant mt-1">Manually enter a daily settlement from a bank statement or terminal report.</DialogDescription>
        <form onSubmit={submit} className="mt-5 space-y-4">
          <Field label="Terminal">
            <Select value={terminalId} onValueChange={setTerminalId}>
              <SelectTrigger data-testid="settlement-terminal" className="w-full bg-surface-containerHigh border-b-2 border-outline focus:border-primary rounded-t-lg h-11 text-sm"><SelectValue placeholder="Select terminal" /></SelectTrigger>
              <SelectContent className="rounded-2xl bg-surface">
                {terminals.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date">
              <input type="date" data-testid="settlement-date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-transparent outline-none w-full text-surface-on py-2 border-b-2 border-outline focus:border-primary" />
            </Field>
            <Field label="Tx count (opt)">
              <input type="number" min="0" value={txCount} onChange={(e) => setTxCount(e.target.value)} className="bg-transparent outline-none w-full text-surface-on py-2 border-b-2 border-outline focus:border-primary" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Gross (A$)">
              <input type="number" step="0.01" data-testid="settlement-gross" value={gross} onChange={(e) => setGross(e.target.value)} placeholder="0.00" className="bg-transparent outline-none w-full text-surface-on py-2 border-b-2 border-outline focus:border-primary" />
            </Field>
            <Field label="Fees (A$)">
              <input type="number" step="0.01" data-testid="settlement-fees" value={fees} onChange={(e) => setFees(e.target.value)} placeholder="0.00" className="bg-transparent outline-none w-full text-surface-on py-2 border-b-2 border-outline focus:border-primary" />
            </Field>
          </div>
          <Field label="External reference (optional)">
            <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Batch ID, deposit ref" className="bg-transparent outline-none w-full text-surface-on py-2 border-b-2 border-outline focus:border-primary" />
          </Field>
          <button data-testid="settlement-save" type="submit" disabled={saving || !terminalId || !gross} className="w-full rounded-full bg-primary text-primary-foreground font-medium py-3 active:scale-[0.98] hover:brightness-110 disabled:opacity-60 transition-[transform,filter] duration-200 ease-md">
            {saving ? "Saving…" : "Record settlement"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-medium tracking-widest uppercase text-surface-onVariant mb-1">{label}</div>
      {children}
    </div>
  );
}
